import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { GET_MEMBERS } from '../services/members';
import { DEFAULT_LANGUAGE } from '../data/kinshipTranslations';
import { buildPedigree } from '../utils/buildPedigree';
import FamilyTree from '../components/tree/FamilyTree';
import TreeControls, { MobileZoomControls } from '../components/tree/TreeControls';
import TreeBreadcrumb from '../components/tree/TreeBreadcrumb';
import authStorage from '../store/authStorage';

function TreePage() {
  const treeRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [zoomLevel, setZoomLevel] = useState(1);
  const [transitioning, setTransitioning] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(DEFAULT_LANGUAGE);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Fetch all members
  const [fetchMembers, { data: membersData }] = useLazyQuery(GET_MEMBERS, {
    fetchPolicy: 'network-only',
    variables: { offset: 0, limit: 500 },
  });

  // Get current user's personId
  const getPersonId = () => {
    try {
      const token = authStorage.getToken();
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.personId || localStorage.getItem('mien_person_id');
      }
    } catch {}
    return localStorage.getItem('mien_person_id');
  };
  const myId = getPersonId();

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const members = membersData?.members?.members || [];

  // Build pedigree from real data
  const pedigreeData = buildPedigree(members, myId);

  const navStack = [{ person: pedigreeData?.root || { id: 'empty', name: 'Loading...', gender: 'MALE', isLiving: true, kinshipTerm: '' }, pedigreeData: pedigreeData || { root: { id: 'empty', name: 'Loading...', gender: 'MALE', isLiving: true }, rootSiblings: [], rootChildren: [], parentGeneration: { father: { id: 'empty-f', name: 'Father', gender: 'MALE' }, mother: { id: 'empty-m', name: 'Mother', gender: 'FEMALE' }, fatherSiblings: [], motherSiblings: [] }, grandparentGeneration: { paternalGrandfather: { id: 'empty-pgf', name: 'GF', gender: 'MALE' }, paternalGrandmother: { id: 'empty-pgm', name: 'GM', gender: 'FEMALE' }, maternalGrandfather: { id: 'empty-mgf', name: 'GF', gender: 'MALE' }, maternalGrandmother: { id: 'empty-mgm', name: 'GM', gender: 'FEMALE' } } } }];

  const currentNav = navStack[navStack.length - 1];
  const currentPedigree = currentNav.pedigreeData;

  const handleViewFamily = useCallback((person) => {
    // No-op for now — would navigate into a sub-family view
  }, []);

  const handleBack = useCallback(() => {
    // Single-level tree for now
  }, []);

  const handleZoomIn = () => {
    treeRef.current?.zoomIn();
    setZoomLevel(z => Math.min(z + 0.3, 3));
  };

  const handleZoomOut = () => {
    treeRef.current?.zoomOut();
    setZoomLevel(z => Math.max(z - 0.3, 0.3));
  };

  const handleResetView = () => {
    treeRef.current?.resetView();
    setZoomLevel(1);
  };

  const query = searchQuery?.toLowerCase() || '';

  if (!pedigreeData) {
    return (
      <div className="flex flex-col h-[calc(100vh-3.5rem)] items-center justify-center text-gray-500">
        <p className="text-lg mb-2">No family tree data</p>
        <p className="text-sm">Add family members to see your tree.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)]">
      {/* ── Toolbar ── */}
      <TreeControls
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedLanguage={selectedLanguage}
        onLanguageChange={(code) => { setSelectedLanguage(code); setShowLanguageDropdown(false); }}
        showLanguageDropdown={showLanguageDropdown}
        onToggleLanguageDropdown={() => setShowLanguageDropdown(!showLanguageDropdown)}
        zoomLevel={zoomLevel}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onResetView={handleResetView}
        showBackButton={false}
        onBack={handleBack}
      />

      {/* ── Tree Canvas ── */}
      <div className="flex-1 relative bg-gray-50 overflow-hidden">
        {/* ── Mobile floating zoom controls ── */}
        <MobileZoomControls zoomLevel={zoomLevel} onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} onResetView={handleResetView} />

        <FamilyTree
          ref={treeRef}
          pedigreeData={currentPedigree}
          onViewFamily={handleViewFamily}
          query={query}
          selectedLanguage={selectedLanguage}
          activePersonId={myId}
        />
      </div>
    </div>
  );
}

export default TreePage;